import { Modal, Switch } from '@lib/components'
import { FileSvg } from '@lib/images'
import { Button, ThemeColors } from '@octopus-do/octopus-storybook'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import cx from 'classnames'
import type React from 'react'
import { useSelector } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { normalize } from '@/helpers'
import type { ModalProps } from '@/types/modal'
import css from './ExportAsCsv.module.scss'
import { useControls, useSubmitExportAsCsv } from './hooks'

const ExportAsCsv: React.FC<ModalProps> = ({ onClose, onBack }) => {
  const projectName = useSelector(migrationProjectSelector.getProjectTitle)

  const { actions, state } = useControls()
  const { handleSubmit } = useSubmitExportAsCsv(state)

  const handleClick = ({
    action,
    format,
  }: {
    action: Function
    format: string
  }) => {
    sendGtmEvent(GtmTriggerName.EXPORT_CSV, 'Click on export as CSV')

    action()
      .then((projectBlob: Blob) => {
        const link = document.createElement('a')

        link.download = `${normalize(projectName)}.${format}`
        link.href = window.URL.createObjectURL(projectBlob)
        link.click()

        window.URL.revokeObjectURL(link.href)

        return projectBlob
      })
      .catch((e: any) => {
        console.error(e)
      })
  }

  return (
    <Modal
      title="Export as CSV"
      onClose={onClose}
      onBack={onBack}
      width={352}
      image={<FileSvg />}
      classnames={{
        content: css.modalContent,
      }}
    >
      <div className={ThemeColors.light}>
        <div className={css.container}>
          <div className={css.item}>
            <span className={cx(css.title, css.disabled)}>Pages & Blocks</span>
            <Switch disabled checked />
          </div>

          <div className={css.item}>
            <span className={css.title}>Content</span>
            <Switch checked={state.isNotes} onChange={actions.setNotes} />
          </div>

          <div className={css.item}>
            <span className={css.title}>SEO Tags</span>
            <Switch
              checked={state.isSeoMetaTags}
              onChange={actions.setSeoMeta}
            />
          </div>

          <div className={css.item}>
            <span className={css.title}>Column names</span>
            <Switch
              checked={state.isHeadersSet}
              onChange={actions.setHeaders}
            />
          </div>

          <div className={css.item}>
            <span className={css.title}>Links</span>
            <Switch checked={state.isLinks} onChange={actions.setLinks} />
          </div>

          <div className={css.item}>
            <span className={css.title}>Separate by comma</span>
            <Switch
              checked={state.isCommaSeparated}
              onChange={actions.setCommaSeparator}
            />
          </div>
        </div>

        <div className={css.footer}>
          <Button
            label="Save"
            onClick={() => handleClick({ action: handleSubmit, format: 'csv' })}
          />
        </div>
      </div>
    </Modal>
  )
}

export default ExportAsCsv
